import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, Loader2, AlertCircle, Clock, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import type { ComplianceReport } from "@/types/api";

interface ReportTableProps {
  reports: ComplianceReport[];
  isLoading: boolean;
  onDownload: (reportId: string) => void;
  downloadingId: string | null;
}

const statusConfig: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ComponentType<{ className?: string }> }
> = {
  pending: { label: "Pending", variant: "secondary", icon: Clock },
  generating: { label: "Generating", variant: "outline", icon: Loader2 },
  completed: { label: "Completed", variant: "default", icon: CheckCircle2 },
  failed: { label: "Failed", variant: "destructive", icon: AlertCircle },
};

function formatBytes(bytes: number | null): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ReportTable({
  reports,
  isLoading,
  onDownload,
  downloadingId,
}: ReportTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
        <CheckCircle2 className="h-8 w-8 text-muted-foreground" />
        <h3 className="mt-4 text-sm font-semibold">No reports yet</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Generate your first SOC 2 compliance report above
        </p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Period</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>PII</TableHead>
          <TableHead>Size</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {reports.map((report) => {
          const config = statusConfig[report.status] ?? statusConfig.pending;
          const StatusIcon = config.icon;

          return (
            <TableRow key={report.id}>
              <TableCell className="font-medium">
                {format(new Date(report.period_start), "MMM d, yyyy")} –{" "}
                {format(new Date(report.period_end), "MMM d, yyyy")}
              </TableCell>
              <TableCell className="uppercase text-xs">{report.report_type}</TableCell>
              <TableCell>
                <Badge variant={config.variant} className="gap-1">
                  <StatusIcon
                    className={`h-3 w-3 ${report.status === "generating" ? "animate-spin" : ""}`}
                  />
                  {config.label}
                </Badge>
              </TableCell>
              <TableCell>{report.include_pii ? "Yes" : "No"}</TableCell>
              <TableCell>{formatBytes(report.file_size_bytes)}</TableCell>
              <TableCell className="text-muted-foreground">
                {format(new Date(report.created_at), "MMM d, yyyy HH:mm")}
              </TableCell>
              <TableCell className="text-right">
                {report.status === "completed" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Download report"
                    onClick={() => onDownload(report.id)}
                    disabled={downloadingId === report.id}
                  >
                    {downloadingId === report.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                )}
                {report.status === "failed" && report.error_message && (
                  <span className="text-xs text-red-400" title={report.error_message}>
                    {report.error_message.slice(0, 50)}
                  </span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
